<script setup lang="ts">
import type { StepperOption } from "#bb/types/blocks/content/StepperOption";
import { eventBus } from "#bb/lib/eventBus";
import { resolveNavigationTarget } from "#bb/lib/navigationTarget";

const props = defineProps<{ id: string }>();

const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id) as StepperOption);


const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Building block editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the stepper",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "heroicons:pencil",
      },
    ],
  },
];

// Expose the commands and properties
defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Stepper",
    icon: "lucide:circle-arrow-right",
  },
});

const route = useRoute();
const features = inject<{
  resourceDefinitionPath?: string | null;
}>("render-features", {});
const { resolveHref } = useAppRoute();
const getStepPath = (step: {
  path?: string;
  resourcePath?: string;
  resourceId?: string;
}) => {
  const rawPath = (step.path || step.resourcePath || step.resourceId || "").trim();

  if (!rawPath) {
    return route.fullPath;
  }

  return resolveNavigationTarget(rawPath, {
    currentPath: route.fullPath,
    resourceDefinitionPath: features.resourceDefinitionPath,
    resolvePath: resolveHref,
  })?.href ?? route.fullPath;
};
</script>

<template>
  <div class="@min-[1024px]:border-b @min-[1024px]:border-t @min-[1024px]:border-gray-200">
    <nav class="mx-auto max-w-7xl px-4 @min-[640px]:px-6 @min-[1024px]:px-8" aria-label="Progress">
      <ol
        role="list"
        class="overflow-hidden rounded-md @min-[1024px]:flex @min-[1024px]:rounded-none @min-[1024px]:border-l @min-[1024px]:border-r @min-[1024px]:border-gray-200"
      >
        <li
          v-for="(step, stepIdx) in definitionData?.steps ?? []"
          :key="step.id"
          class="relative overflow-hidden @min-[1024px]:flex-1"
        >
          <div
            :class="[
              stepIdx === 0 ? 'rounded-t-md border-b-0' : '',
              stepIdx === (definitionData?.steps?.length ?? 0) - 1 ? 'rounded-b-md border-t-0' : '',
              'overflow-hidden border border-gray-200 @min-[1024px]:border-0',
            ]"
          >
            <nuxt-link
              v-if="step.status === 'completed'"
              :to="getStepPath(step)"
              class="group"
            >
              <span
                class="absolute left-0 top-0 h-full w-1 bg-transparent group-hover:bg-gray-200 @min-[1024px]:bottom-0 @min-[1024px]:top-auto @min-[1024px]:h-1 @min-[1024px]:w-full"
                aria-hidden="true"
              />
              <span
                :class="[
                  stepIdx !== 0 ? '@min-[1024px]:pl-9' : '',
                  'flex items-start px-6 py-5 text-sm font-medium',
                ]"
              >
                <span class="flex-shrink-0">
                  <span
                    class="flex h-10 w-10 items-center justify-center rounded-full bg-primary"
                  >
                    <icon mode="svg" size="24"
                      name="lucide:check"
                      class="text-white"
                      aria-hidden="true"
                    />
                  </span>
                </span>
                <span class="ml-4 mt-0.5 flex min-w-0 flex-col">
                  <span class="text-sm font-medium">{{ step.title }}</span>
                  <span class="text-sm font-medium text-gray-500">{{
                    step.description
                  }}</span>
                </span>
              </span>
            </nuxt-link>
            <nuxt-link
              v-else-if="step.status === 'active'"
              :to="getStepPath(step)"
              aria-current="step"
            >
              <span
                class="absolute left-0 top-0 h-full w-1 bg-primary @min-[1024px]:bottom-0 @min-[1024px]:top-auto @min-[1024px]:h-1 @min-[1024px]:w-full"
                aria-hidden="true"
              />
              <span
                :class="[
                  stepIdx !== 0 ? '@min-[1024px]:pl-9' : '',
                  'flex items-start px-6 py-5 text-sm font-medium',
                ]"
              >
                <span class="flex-shrink-0">
                  <span
                    class="flex h-10 w-10 items-center justify-center rounded-full border-2 border-primary"
                  >
                    <span class="text-primary">{{ step.number }}</span>
                  </span>
                </span>
                <span class="ml-4 mt-0.5 flex min-w-0 flex-col">
                  <span class="text-sm font-medium text-primary">{{
                    step.title
                  }}</span>
                  <span class="text-sm font-medium text-gray-500">{{
                    step.description
                  }}</span>
                </span>
              </span>
            </nuxt-link>
            <nuxt-link
              v-else
              :to="getStepPath(step)"
              class="group"
            >
              <span
                class="absolute left-0 top-0 h-full w-1 bg-transparent group-hover:bg-gray-200 @min-[1024px]:bottom-0 @min-[1024px]:top-auto @min-[1024px]:h-1 @min-[1024px]:w-full"
                aria-hidden="true"
              />
              <span
                :class="[
                  stepIdx !== 0 ? '@min-[1024px]:pl-9' : '',
                  'flex items-start px-6 py-5 text-sm font-medium',
                ]"
              >
                <span class="flex-shrink-0">
                  <span
                    class="flex h-10 w-10 items-center justify-center rounded-full border-2 border-gray-300"
                  >
                    <span class="text-gray-500">{{ step.number }}</span>
                  </span>
                </span>
                <span class="ml-4 mt-0.5 flex min-w-0 flex-col">
                  <span class="text-sm font-medium text-gray-500">{{
                    step.title
                  }}</span>
                  <span class="text-sm font-medium text-gray-500">{{
                    step.description
                  }}</span>
                </span>
              </span>
            </nuxt-link>
            <template v-if="stepIdx !== 0">
              <!-- Separator -->
              <div
                class="absolute inset-0 left-0 top-0 hidden w-3 @min-[1024px]:block"
                aria-hidden="true"
              >
                <svg
                  class="h-full w-full text-gray-300"
                  viewBox="0 0 12 82"
                  fill="none"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M0.5 0V31L10.5 41L0.5 51V82"
                    stroke="currentcolor"
                    vector-effect="non-scaling-stroke"
                  />
                </svg>
              </div>
            </template>
          </div>
        </li>
      </ol>
    </nav>
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

export default {
  componentId: ComponentDefinitionIds.NAVIGATION_STEPPER_02,
  icon: "lucide:circle-arrow-right",
  type: "Stepper 02",
  description: "A detailed multi-step navigation progress component.",
  editor: () => import("#bb/components/editor/navigation/stepper/02.vue"),
  default: {
    steps: [
      {
        id: "step-1",
        number: "1",
        title: "Step 1",
        description: "First step",
        path: "/1",
        status: "active",
      },
      {
        id: "step-2",
        number: "2",
        title: "Step 2",
        description: "Second step",
        path: "/2",
        status: "upcoming",
      },
    ],
  },
  categories: ["Navigation"],
  order: 160103,
};
</script>
